"use client";

import { createContext, useContext } from "react";
import * as stylex from "@stylexjs/stylex";
import "@/colors.stylex";
import { colors } from "@/colors.stylex";
import { GoBoard } from "@/components/go-board";
import { MokaAdvantageBar } from "@/components/moka-advantage-bar";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { MOKA_ROLLOUT_AVERAGE_MOVE_DECIMAL_COUNT, MOKA_ROLLOUT_DURATION_DECIMAL_COUNT, MOKA_ROLLOUT_GAME_COUNT_OPTIONS, MILLISECONDS_PER_SECOND, MOKA_ROLLOUT_RESULT_OPTIONS } from "@/constants";
import { useMokaRollouts } from "@/hooks/use-moka-rollouts";
import { sharedStyles } from "@/shared-styles";
const MOKA_ROLLOUTS_HEADING_ID = "moka-rollouts-heading";
const MOKA_ROLLOUTS_GAME_COUNT_ID = "moka-rollout-game-count";
const styles = {
  section: {
    kLKAdn: "x1b58sdr",
    $$css: true
  },
  runButton: {
    kZKoxP: "x12hms15",
    k1C7PZ: "x4prdry",
    $$css: true
  }
};
const MokaRolloutsContext = createContext(null);
const useMokaRolloutsContext = () => {
  const mokaRolloutsContext = useContext(MokaRolloutsContext);
  if (!mokaRolloutsContext) {
    throw new Error("MokaRollouts components must be rendered inside MokaRolloutsRoot.");
  }
  return mokaRolloutsContext;
};
const MokaRolloutsRoot = ({
  children
}) => <MokaRolloutsContext value={useMokaRollouts()}>
    <section aria-labelledby={MOKA_ROLLOUTS_HEADING_ID} {...stylex.props(sharedStyles.stack, styles.section)}>
      {children}
    </section>
  </MokaRolloutsContext>;
MokaRolloutsRoot.displayName = "MokaRolloutsRoot";
const MokaRolloutsHeading = ({
  children
}) => <h2 id={MOKA_ROLLOUTS_HEADING_ID} {...stylex.props(sharedStyles.sectionHeading)}>
    {children}
  </h2>;
MokaRolloutsHeading.displayName = "MokaRolloutsHeading";
const MokaRolloutsForm = () => {
  const {
    completedGames,
    gameCount,
    isRunning,
    runRollouts,
    setGameCount,
    status
  } = useMokaRolloutsContext();
  return <form onSubmit={event => {
    event.preventDefault();
    void runRollouts();
  }} className="x78zum5 x1a02dak x6s0dn4 x13z6uf9">
      <label htmlFor={MOKA_ROLLOUTS_GAME_COUNT_ID} {...stylex.props(sharedStyles.srOnly)}>
        Number of games
      </label>
      <div className="x1n2onr6 x12hms15">
        <select disabled={isRunning} id={MOKA_ROLLOUTS_GAME_COUNT_ID} onChange={event => setGameCount(Number(event.currentTarget.value))} value={gameCount} className="x12hms15 xjyslct xtkphhm xmkeg23 x1y0btm7 x1335ttx xp2616n xt970qd xxcd0se x9dzeaa xmotr4s xkpwil5 xo5v014 xo1l8bm x1gx1wh3 x784prv x1t137rt x9v5kkp x1s0p2oj x7s97pk xfqoyci">
          {MOKA_ROLLOUT_GAME_COUNT_OPTIONS.map(gameCountOption => <option key={gameCountOption} value={gameCountOption}>
              {gameCountOption} games
            </option>)}
        </select>
        <span aria-hidden="true" {...{
        0: {
          className: "x47corl x10l6tqk xwa60dl x1u62bzc x1jjc1wv x164u9eo x3sa99s x1yl65le xs1s249 x32b0ac xocfoax xso031l x1q0q8m5 x1l78zn4"
        },
        1: {
          className: "x47corl x10l6tqk xwa60dl x1u62bzc x1jjc1wv x164u9eo x3sa99s x1yl65le xs1s249 x32b0ac xocfoax xso031l x1q0q8m5 x1l78zn4 x197sbye"
        }
      }[!!isRunning << 0]} />
      </div>
      <Button disabled={isRunning} styles={styles.runButton} type="submit">
        {isRunning && <Spinner />}
        {status === "loading" ? "Loading Moka…" : status === "running" ? `Running ${completedGames.length}/${gameCount}…` : "Run rollouts"}
      </Button>
    </form>;
};
MokaRolloutsForm.displayName = "MokaRolloutsForm";
const MokaRolloutsResultFilter = ({
  resultOption
}) => {
  const {
    completedGames,
    highlightedResult,
    selectedResult,
    setHoveredResult,
    setSelectedResult
  } = useMokaRolloutsContext();
  const resultCount = completedGames.filter(game => game.result === resultOption.result).length;
  return <button aria-pressed={selectedResult === resultOption.result} onBlur={() => setHoveredResult(null)} onClick={() => setSelectedResult(currentResult => currentResult === resultOption.result ? null : resultOption.result)} onFocus={() => setHoveredResult(resultOption.result)} onMouseEnter={() => setHoveredResult(resultOption.result)} onMouseLeave={() => setHoveredResult(null)} type="button" {...{
    0: {
      className: "x16eypsj x3pynha xtnl7vc xx6bhzk xwwtwea x784prv x1t137rt x9v5kkp x1s0p2oj x7s97pk x1ygoef6"
    },
    1: {
      className: "x16eypsj x3pynha xtnl7vc xx6bhzk xwwtwea x784prv x1t137rt x9v5kkp x1s0p2oj x7s97pk x1ngwgic xujl8zx"
    }
  }[!!(highlightedResult === resultOption.result) << 0]}>
      {resultOption.label} {resultCount}
    </button>;
};
MokaRolloutsResultFilter.displayName = "MokaRolloutsResultFilter";
const MokaRolloutsSummary = () => {
  const {
    completedGames,
    durationMs
  } = useMokaRolloutsContext();
  const moveCapCount = completedGames.filter(game => game.didReachMoveCap).length;
  const averageMoveCount = completedGames.length === 0 ? 0 : completedGames.reduce((moveCountSum, game) => moveCountSum + game.gameState.moveCount, 0) / completedGames.length;
  return <>
      {MOKA_ROLLOUT_RESULT_OPTIONS.map((resultOption, resultOptionIndex) => <span key={resultOption.result}>
          {resultOptionIndex > 0 && " · "}
          <MokaRolloutsResultFilter resultOption={resultOption} />
        </span>)}
      {` · ${averageMoveCount.toFixed(MOKA_ROLLOUT_AVERAGE_MOVE_DECIMAL_COUNT)} moves average · ${moveCapCount} capped · ${(durationMs / MILLISECONDS_PER_SECOND).toFixed(MOKA_ROLLOUT_DURATION_DECIMAL_COUNT)}s`}
    </>;
};
MokaRolloutsSummary.displayName = "MokaRolloutsSummary";
const MokaRolloutsStatus = () => {
  const {
    completedGames,
    errorMessage,
    gameCount,
    status
  } = useMokaRolloutsContext();
  return <p aria-live="polite" className="xncyony">
      {errorMessage ?? (status === "idle" ? "Ready." : status === "loading" ? "Loading the model…" : status === "running" ? `${completedGames.length} of ${gameCount} games complete.` : null)}
      {!errorMessage && status === "complete" && <MokaRolloutsSummary />}
    </p>;
};
MokaRolloutsStatus.displayName = "MokaRolloutsStatus";
const MokaRolloutsBoard = ({
  game
}) => {
  const {
    highlightedResult
  } = useMokaRolloutsContext();
  const boardStatus = game.result ? game.result === "draw" ? "draw" : `${game.result} won` : `move ${game.gameState.moveCount}`;
  const boardDetails = `${boardStatus} · ${game.gameState.moveCount} moves${game.didReachMoveCap ? " · capped" : ""}`;
  const isDeemphasized = highlightedResult !== null && game.result !== highlightedResult;
  return <figure aria-label={`Rollout ${game.id + 1}, ${boardDetails}`} role="img" tabIndex={0} {...{
    0: {
      className: "x1n2onr6 x1ctf4ec x19991ni xx6bhzk xwwtwea x784prv x1t137rt x9v5kkp x1s0p2oj x7s97pk x9my540 x1tuxu5l xun2g61 x1fe3he6 x1p7t2yv xbtxdup"
    },
    1: {
      className: "x1n2onr6 x1ctf4ec x19991ni xx6bhzk xwwtwea x784prv x1t137rt x9v5kkp x1s0p2oj x7s97pk x9my540 x1tuxu5l xun2g61 x1fe3he6 x1p7t2yv xbtxdup xvpkmg4"
    }
  }[!!isDeemphasized << 0]}>
      <MokaAdvantageBar blackWinProbability={game.blackWinProbability} variant="compact" winner={game.result === "draw" ? null : game.result} />
      <GoBoard aria-hidden="true" size="fluid" stones={game.stones} variant="monochrome" />
      <figcaption className="x47corl x10l6tqk x6fl0gr x5qeokm x1mgtmex x9fksmc x6i6fhv xejloyu x18a9hih x1o5vn8x x2b8uid xmotr4s x1k6wstc x132q4wb xo1l8bm x6ikql9 x10fvgnw x1l0vmcy x1dmgsgi xelvad xx6bhzk xwwtwea">{boardDetails}</figcaption>
    </figure>;
};
MokaRolloutsBoard.displayName = "MokaRolloutsBoard";
const MokaRolloutsGrid = () => {
  const {
    games
  } = useMokaRolloutsContext();
  if (games.length === 0) {
    return null;
  }
  return <div className="xrvj5dj x1sipb5g x1cqhhh2 x8233eu">
      {games.map(game => <MokaRolloutsBoard game={game} key={game.id} />)}
    </div>;
};
MokaRolloutsGrid.displayName = "MokaRolloutsGrid";
export { MokaRolloutsForm, MokaRolloutsGrid, MokaRolloutsHeading, MokaRolloutsRoot, MokaRolloutsStatus };